import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

import { useConnectCode } from '@/hooks/useConnectCode';
import { tr, useT } from '@/i18n';
import { copyText } from '@/lib/clipboard';
import { bridgePath, connectLink, shareLink } from '@/lib/links';
import { platform } from '@platform';
import styles from './Share.module.css';
import shared from '@/pages/pages.module.css';

import { BackButton } from './BackButton';
import { Button } from './Button';
import { ArrowRightIcon, CheckIcon, CopyIcon } from './Icons';
import { NearbySend } from './NearbySend';
import { QrImage } from './QrImage';
import { ShareCodePanel } from './ShareCodePanel';
import type { CompareStep, ShareOverlayProps, ShareTab } from './ShareOverlay.types';
import { TABS, wordingOf } from './shareWording';
import { useToast } from './Toast';
import ui from './ui.module.css';

/** Sharing a bridge — Link, QR code, Code and Nearby: the screen after creating it, or the Share dropdown (`variant`). */
export function ShareOverlay({ bridgeId, bridgeKey, ownerToken, viewsLeft, waiting = false, purpose = 'chat', connectRequest, onClose, onDeviceConnected, variant = 'page' }: ShareOverlayProps): React.JSX.Element {
  const t = useT();
  const page = variant === 'page';
  const toast = useToast();
  const navigate = useNavigate();
  const [tab, setTab] = useState<ShareTab>('link');
  const [copied, setCopied] = useState(false);
  const [copiedAddress, setCopiedAddress] = useState(false);
  // the Nearby step that waits for “match”, and the code step that does, reported to the one button in the footer
  const [nearbyCompare, setNearbyCompare] = useState<CompareStep | null>(null);
  const [confirming, setConfirming] = useState(false);
  const link = shareLink(bridgeId, bridgeKey);
  const connect = useConnectCode({ bridgeId, bridgeKey, ownerToken, tab, connectRequest, onDeviceConnected });

  const copy = async (): Promise<void> => {
    const ok = await copyText(link);
    if (ok) {
      // the copy button turns into a green check for a moment: no notice anywhere else
      setCopied(true);
      window.setTimeout(() => {
        setCopied(false);
      }, 1800);
    } else {
      toast.error(t('shareOverlay.copyFailed'));
    }
  };

  const copyConnectUrl = async (): Promise<void> => {
    if (await copyText(connectLink())) {
      setCopiedAddress(true);
      window.setTimeout(() => {
        setCopiedAddress(false);
      }, 1800);
    } else toast.error(t('shareOverlay.copyAddressFailed'));
  };

  const words = wordingOf(waiting, purpose);
  const { pending, allow } = connect;
  const codeCompare = useMemo<CompareStep | null>(
    () =>
      tab === 'code' && pending !== null
        ? {
            status: confirming ? 'busy' : 'ready',
            peer: pending.device,
            confirm: () => {
              setConfirming(true);
              void allow().finally(() => {
                setConfirming(false);
              });
            },
          }
        : null,
    [tab, pending, confirming, allow],
  );
  // On the screen after creating a bridge the footer's button says the emoji match; in the Share dropdown, which has no footer, the cards keep their own.
  const compare = page ? (tab === 'nearby' ? nearbyCompare : codeCompare) : null;
  // Leaving this screen never discards anything: it goes on to the chat / channel, or to the shared secret.

  return (
    <div className={[styles.share, page ? '' : styles.sharePopover].join(' ')} data-testid={page ? 'share-overlay' : 'share-menu'}>
      {page ? (
        <div className={styles.shareHead}>
          <BackButton />
          <h2 className={shared.panelTitle}>{t(words.title)}</h2>
        </div>
      ) : null}

      <div className={[ui.seg, styles.shareTabs].join(' ')} role="tablist" aria-label={t('shareOverlay.options')}>
        {TABS.map((item) => (
          <button
            key={item.id}
            type="button"
            role="tab"
            id={`share-tab-${item.id}`}
            aria-selected={tab === item.id}
            aria-controls={`share-panel-${item.id}`}
            className={ui.segTab}
            onClick={() => {
              setTab(item.id);
            }}
          >
            {item.icon}
            <span className={ui.segTabLabel} data-label={t(item.label)}>
              {t(item.label)}
            </span>
          </button>
        ))}
      </div>

      {tab === 'link' ? (
        <div id="share-panel-link" role="tabpanel" aria-labelledby="share-tab-link" className={styles.sharePanel}>
          <p className={styles.shareLead}>{t(words.lead)}</p>
          <div className={[styles.linkBox, copied ? styles.linkBoxCopied : ''].join(' ')}>
            <span className={styles.linkText} title={link} data-ltr="" data-testid="share-link">
              {link}
            </span>
            <button type="button" className={[styles.linkCopyBtn, copied ? styles.linkCopied : ''].join(' ')} onClick={() => void copy()} aria-label={copied ? t('common.copied') : t('shareOverlay.copyLink')} title={t('shareOverlay.copyLink')} data-testid="copy-link">
              {copied ? <CheckIcon size={20} /> : <CopyIcon size={20} />}
            </button>
          </div>
          <p className={styles.shareWarn}>{words.warning}</p>
          {platform.isPreview ? (
            // only in the preview, which cannot open its link in another tab: a way to see the recipient's side here
            <p className={styles.demoNote}>
              {tr('shareOverlay.previewNote', {
                open: (children) => (
                  <button
                    type="button"
                    className={styles.linkBtn}
                    onClick={() => {
                      void navigate(`${bridgePath(bridgeId, bridgeKey)}&as=guest`);
                    }}
                    data-testid="preview-guest"
                  >
                    {children}
                  </button>
                ),
              })}
            </p>
          ) : null}
        </div>
      ) : null}

      {tab === 'qr' ? (
        <div id="share-panel-qr" role="tabpanel" aria-labelledby="share-tab-qr" className={[styles.sharePanel, styles.sharePanelCenter].join(' ')}>
          <div className={styles.qrBox}>
            <QrImage value={link} alt={t('shareOverlay.qrAlt')} />
          </div>
          <p className={styles.shareWarn}>
            {t('shareOverlay.qrScan')}
            <br />
            {t('shareOverlay.qrKey')}
          </p>
        </div>
      ) : null}

      {tab === 'nearby' ? (
        <div id="share-panel-nearby" role="tabpanel" aria-labelledby="share-tab-nearby" className={styles.sharePanel}>
          <NearbySend bridgeId={bridgeId} bridgeKey={bridgeKey} ownerToken={ownerToken} viewsLeft={viewsLeft} onSent={onDeviceConnected} onCompare={page ? setNearbyCompare : undefined} />
        </div>
      ) : null}

      {tab === 'code' ? <ShareCodePanel connect={connect} viewsLeft={viewsLeft} copiedAddress={copiedAddress} onCopyAddress={() => void copyConnectUrl()} footerConfirm={page} confirming={confirming} /> : null}

      {page && words.wait !== null ? (
        <p className={styles.waiting} role="status" data-testid="waiting">
          <i aria-hidden="true" />
          {t(words.wait)}
        </p>
      ) : null}
      {page ? (
        <div className={shared.shareFoot}>
          {compare === null ? (
            <Button className={styles.shareDone} onClick={onClose} data-testid="share-done">
              {t(words.close)}
              {waiting ? <ArrowRightIcon size={16} /> : <CheckIcon size={16} />}
            </Button>
          ) : (
            <Button className={styles.shareDone} onClick={compare.confirm} disabled={compare.status !== 'ready'} data-testid="share-done" data-compare={compare.status}>
              {compare.status === 'busy' ? t('share.sendingTo', { peer: compare.peer }) : t(words.match)}
              {compare.status === 'busy' ? null : waiting ? <ArrowRightIcon size={16} /> : <CheckIcon size={16} />}
            </Button>
          )}
        </div>
      ) : null}
    </div>
  );
}
